import { Fragment, useMemo, type ReactNode } from 'react';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import {
  CheckFilterRow,
  FilterSection,
  PriceRangeSection,
  ResultsFiltersPanel,
  type ResultsFiltersPanelHeader,
  SwitchFilterRow,
} from '../../shared/results_page/results_filters_panel.js';
import type { RentalOfferView } from '../models.js';
import { RentalCompanyMark } from '../rental_company_mark.js';

/** Os FATOS de corte de uma oferta, ao lado da view que a lista mostra — o
 *  mesmo desenho do `HotelFilterableOffer`: a view é prosa, os fatos são
 *  números e ids. */
export type RentalFilterableOffer = {
  offer: RentalOfferView;
  withinPolicy: boolean;
  /** O TOTAL do período, em unidades da moeda da lista — o número do card e o
   *  eixo da ordenação por preço. */
  totalPrice?: number;
  /** A diária derivada (total ÷ dias) — o eixo "Faixa de diária". */
  dailyPrice?: number;
  /** O `categoryCode` e o `rentalCode` do fornecedor: são as chaves que o
   *  refino no servidor entende (`rentalServerFiltersOf`). */
  categoryId: string;
  companyId: string;
  /** Quantas preferências do viajante o carro atende (locadora, categoria,
   *  câmbio) — só desempata o critério escolhido: entre iguais nele, os que
   *  atendem mais vêm antes. Ausente = 0. Quem casa é o host, que conhece as
   *  preferências. */
  preferenceRank?: number;
  /** Km em linha reta da agência de retirada até o lugar buscado (a âncora do
   *  mapa). Só ORDENA: nunca vira número na tela, porque a âncora de um
   *  aeroporto é um ponto arbitrário (#5125). Ausente sem as duas coordenadas. */
  distance?: number;
  /** A página da busca que trouxe a oferta (`useRentalOfferPages.pageOf`). A
   *  ordem da lista nunca tira uma oferta da página dela: com a rolagem
   *  infinita, a página nova só ACRESCENTA embaixo (#4513). Ausente = 1. */
  page?: number;
};

/** Os acessórios são os dois fatos que a view já carrega — câmbio automático e
 *  ar-condicionado. Não são uma lista aberta do fornecedor como as comodidades
 *  do hotel, então o eixo é fechado. */
export type RentalAccessory = 'air-conditioning' | 'automatic';
export const RENTAL_ACCESSORIES: readonly RentalAccessory[] = ['air-conditioning', 'automatic'];

export type RentalResultsFilterValue = {
  readonly policyOnly: boolean;
  /** Categorias em `or`: marcar duas é "uma OU outra" — categoria se combina,
   *  como as faixas de nota do hotel. Vazio = eixo neutro. */
  readonly categoryIds: readonly string[];
  /** Acessórios em `and`: quem marca dois requisitos quer os dois. */
  readonly accessories: readonly RentalAccessory[];
  /** Piso/teto da diária. `undefined` = alça na ponta do catálogo. */
  readonly minimumDailyPrice?: number;
  readonly maximumDailyPrice?: number;
  /** Locadoras em `or`. Vazio = eixo neutro. */
  readonly companyIds: readonly string[];
};

export const EMPTY_RENTAL_RESULTS_FILTERS: RentalResultsFilterValue = {
  policyOnly: false,
  categoryIds: [],
  accessories: [],
  companyIds: [],
};

export type RentalResultsFiltersCopy = {
  filtersLabel: string;
  collapseFiltersLabel: string;
  closeFiltersLabel: string;
  resetFiltersLabel: string;
  policyLabel: string;
  withinPolicyLabel: string;
  categoryLabel: string;
  /** A ponta da linha de categoria: o menor total da categoria, já formatado
   *  ("R$ 450"). */
  fromPriceLabel(amount: number): string;
  accessoriesLabel: string;
  accessoryLabel(accessory: RentalAccessory): string;
  dailyPriceLabel: string;
  /** Nomes acessíveis das pontas editáveis da faixa de diária. */
  minimumPriceLabel: string;
  maximumPriceLabel: string;
  companyLabel: string;
};

export type RentalCategoryOption = { id: string; label: string; fromPrice?: number };
export type RentalCompanyOption = { id: string; label: string; logoUrl?: string };

/** As opções de categoria e locadora vindas do SERVIDOR, que conta a busca
 *  inteira (`rentalFilterCatalogOf`) — estáveis enquanto as páginas chegam. */
export type RentalFilterCatalog = {
  readonly categories: readonly RentalCategoryOption[];
  readonly companies: readonly RentalCompanyOption[];
};

export type RentalResultsFiltersProps = ResultsFiltersPanelHeader & {
  offers: readonly RentalFilterableOffer[];
  /** O catálogo do servidor. Ausente ⇒ as opções saem das ofertas carregadas. */
  catalog?: RentalFilterCatalog;
  currency: string;
  value: RentalResultsFilterValue;
  copy: RentalResultsFiltersCopy;
  locked?: boolean;
  /** Ver `ResultsFiltersPanel.frame`. */
  frame?: boolean;
  /** A lista carregada é uma AMOSTRA do fornecedor (o "N de M" do cabeçalho):
   *  nos eixos que viram corte NO SERVIDOR (categoria e locadora, as facetas do
   *  refino), zero local não prova ausência no conjunto — a linha zerada segue
   *  clicável. */
  countsPartial?: boolean;
  /** O pino do mapa filtra a lista (#3995): fora do `value`, mas o `Redefinir`
   *  do cabeçalho conta com ele e o limpa (`onAgencyUnpin`). */
  agencyPinned?: boolean;
  onAgencyUnpin?: () => void;
  onChange(value: RentalResultsFilterValue): void;
};

type FilterAxis =
  | 'policyOnly'
  | 'categoryIds'
  | 'accessories'
  | 'minimumDailyPrice'
  | 'maximumDailyPrice'
  | 'companyIds';

function hasAccessory(offer: RentalOfferView, accessory: RentalAccessory): boolean {
  return accessory === 'automatic' ? offer.transmission === 'automatic' : offer.airConditioning;
}

/** Teto/piso genéricos de eixo numérico: sem dado, a oferta SAI quando há
 *  corte ativo (um número desconhecido não prova que cabe na régua). */
function withinBound(
  value: number | undefined,
  bound: number | undefined,
  side: 'min' | 'max',
  ignored: boolean,
): boolean {
  if (ignored || bound === undefined) return true;
  if (value === undefined) return false;
  return side === 'min' ? value >= bound : value <= bound;
}

/** O corte completo, puro — cada eixo é um `and`; `ignoredAxis` tira UM eixo da
 *  conta, que é o que faz a contagem de cada opção ser honesta (quantos sobram
 *  considerando os OUTROS cortes). */
export function matchesRentalResultsFilters(
  facts: RentalFilterableOffer,
  filters: RentalResultsFilterValue,
  ignoredAxis?: FilterAxis,
): boolean {
  const skip = (axis: FilterAxis) => ignoredAxis === axis;
  return (
    (skip('policyOnly') || !filters.policyOnly || facts.withinPolicy) &&
    (skip('categoryIds') ||
      filters.categoryIds.length === 0 ||
      filters.categoryIds.includes(facts.categoryId)) &&
    (skip('accessories') ||
      filters.accessories.every((accessory) => hasAccessory(facts.offer, accessory))) &&
    withinBound(facts.dailyPrice, filters.minimumDailyPrice, 'min', skip('minimumDailyPrice')) &&
    withinBound(facts.dailyPrice, filters.maximumDailyPrice, 'max', skip('maximumDailyPrice')) &&
    (skip('companyIds') ||
      filters.companyIds.length === 0 ||
      filters.companyIds.includes(facts.companyId))
  );
}

/** Há corte ativo? É o que liga o `Redefinir` do cabeçalho. */
function hasActiveFilters(filters: RentalResultsFilterValue): boolean {
  return (
    filters.policyOnly ||
    filters.categoryIds.length > 0 ||
    filters.accessories.length > 0 ||
    filters.minimumDailyPrice !== undefined ||
    filters.maximumDailyPrice !== undefined ||
    filters.companyIds.length > 0
  );
}

/** O catálogo do servidor primeiro, completo e estável; o que só as ofertas
 *  carregadas conhecem entra depois (a categoria "Outros", que as facetas do
 *  servidor não contam). */
function withLoaded<T extends { id: string }>(
  server: readonly T[] | undefined,
  loaded: readonly T[],
): T[] {
  if (!server) return [...loaded];
  const known = new Set(server.map(({ id }) => id));
  return [...server, ...loaded.filter(({ id }) => !known.has(id))];
}

/** As réguas que saem do CATÁLOGO em cena: a faixa de diária (piso/teto do
 *  slider), as categorias com o menor total de cada uma (ordenadas por ele,
 *  como no frame) e as locadoras em ordem alfabética — filtro que oferece opção
 *  inexistente devolve lista vazia sem explicar por quê. Com o catálogo do
 *  servidor, categorias e locadoras saem dele (#4513). */
export function useRentalFilterCatalog(
  offers: readonly RentalFilterableOffer[],
  catalog: RentalFilterCatalog | undefined,
) {
  return useMemo(() => {
    const prices = offers.flatMap(({ dailyPrice }) =>
      dailyPrice === undefined ? [] : [dailyPrice],
    );
    const categories = new Map<string, RentalCategoryOption>();
    const companies = new Map<string, RentalCompanyOption>();
    offers.forEach(({ offer, categoryId, companyId, totalPrice }) => {
      const category = categories.get(categoryId);
      if (!category) {
        categories.set(categoryId, {
          id: categoryId,
          label: offer.categoryLabel,
          fromPrice: totalPrice,
        });
      } else if (totalPrice !== undefined) {
        category.fromPrice =
          category.fromPrice === undefined ? totalPrice : Math.min(category.fromPrice, totalPrice);
      }
      if (!companies.has(companyId)) {
        companies.set(companyId, {
          id: companyId,
          label: offer.company.name,
          logoUrl: offer.company.logoUrl,
        });
      }
    });
    const byPrice = (a: RentalCategoryOption, b: RentalCategoryOption) =>
      (a.fromPrice ?? Number.POSITIVE_INFINITY) - (b.fromPrice ?? Number.POSITIVE_INFINITY);
    return {
      priceFloor: prices.length === 0 ? undefined : Math.min(...prices),
      priceCeiling: prices.length === 0 ? undefined : Math.max(...prices),
      categories: withLoaded(catalog?.categories, [...categories.values()]).sort(byPrice),
      companies: withLoaded(catalog?.companies, [...companies.values()]).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    };
  }, [offers, catalog]);
}

function toggled<T>(list: readonly T[], item: T): T[] {
  return list.includes(item) ? list.filter((candidate) => candidate !== item) : [...list, item];
}

/** O que cada eixo precisa para desenhar suas linhas — o painel e as folhas
 *  dos chips passam o MESMO valor (o aplicado ou o rascunho da folha). */
export type RentalFilterAxisProps = Pick<
  RentalResultsFiltersProps,
  'offers' | 'value' | 'copy' | 'locked' | 'countsPartial' | 'onChange'
>;

/** Quantos sobram com a opção, considerando os OUTROS cortes. Só com a busca
 *  INTEIRA em mãos: com a lista parcial a contagem mediria o carregado e
 *  mudaria a cada página da rolagem (#4513, I2). */
function countMatching(
  { offers, value, countsPartial }: RentalFilterAxisProps,
  ignoredAxis: FilterAxis,
  matchesAxis: (facts: RentalFilterableOffer) => boolean,
): { count?: number } {
  return countsPartial
    ? {}
    : {
        count: offers.filter(
          (facts) => matchesRentalResultsFilters(facts, value, ignoredAxis) && matchesAxis(facts),
        ).length,
      };
}

/** As linhas de categoria: o menor total de cada uma na ponta, não a
 *  contagem — e é faceta do refino no servidor: com a lista parcial, a linha
 *  segue clicável mesmo sem oferta local. */
export function RentalCategoryRows({
  categories,
  value,
  copy,
  locked,
  countsPartial,
  onChange,
}: Omit<RentalFilterAxisProps, 'offers'> & {
  readonly categories: readonly RentalCategoryOption[];
}): ReactNode {
  return (
    <div className="-mx-1 grid">
      {categories.map(({ id, label, fromPrice }) => (
        <CheckFilterRow
          key={id}
          label={label}
          checked={value.categoryIds.includes(id)}
          {...(fromPrice === undefined ? {} : { detail: copy.fromPriceLabel(fromPrice) })}
          disabled={locked}
          zeroSelectable={countsPartial}
          onToggle={() => {
            onChange({ ...value, categoryIds: toggled(value.categoryIds, id) });
          }}
        />
      ))}
    </div>
  );
}

/** As linhas de locadora, com a contagem de quem sobra em cada uma. */
export function RentalCompanyRows({
  companies,
  ...axis
}: RentalFilterAxisProps & { readonly companies: readonly RentalCompanyOption[] }): ReactNode {
  const { value, locked, countsPartial, onChange } = axis;
  return (
    <div className="-mx-1 grid">
      {companies.map(({ id, label, logoUrl }) => (
        <CheckFilterRow
          key={id}
          label={label}
          glyph={<RentalCompanyMark url={logoUrl} />}
          checked={value.companyIds.includes(id)}
          {...countMatching(axis, 'companyIds', (facts) => facts.companyId === id)}
          disabled={locked}
          zeroSelectable={countsPartial}
          onToggle={() => {
            onChange({ ...value, companyIds: toggled(value.companyIds, id) });
          }}
        />
      ))}
    </div>
  );
}

/** A faixa de diária do carro sobre a faixa de preço da casca: só o mapeamento
 *  do par piso/teto do valor do filtro. */
export function RentalDailyPriceRange({
  floor,
  ceiling,
  currency,
  value,
  copy,
  locked,
  onChange,
}: Pick<RentalResultsFiltersProps, 'currency' | 'value' | 'copy' | 'locked' | 'onChange'> & {
  readonly floor: number;
  readonly ceiling: number;
}): ReactNode {
  return (
    <PriceRangeSection
      title={copy.dailyPriceLabel}
      floor={floor}
      ceiling={ceiling}
      currency={currency}
      value={{
        ...(value.minimumDailyPrice === undefined ? {} : { minimum: value.minimumDailyPrice }),
        ...(value.maximumDailyPrice === undefined ? {} : { maximum: value.maximumDailyPrice }),
      }}
      minimumLabel={copy.minimumPriceLabel}
      maximumLabel={copy.maximumPriceLabel}
      locked={locked}
      onChange={({ minimum, maximum }) => {
        onChange({ ...value, minimumDailyPrice: minimum, maximumDailyPrice: maximum });
      }}
    />
  );
}

/** Um bloco do painel abaixo do primeiro: o divisor e a seção nomeada. */
function PanelSection({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <Fragment>
      <Separator />
      <FilterSection title={title}>{children}</FilterSection>
    </Fragment>
  );
}

/** O corpo rolável da coluna: os blocos na ordem do frame da #5125 (política →
 *  acessórios → diária → categoria → agência), cada um condicionado ao dado que
 *  o sustenta. */
function RentalResultsFiltersBody(
  props: RentalFilterAxisProps & Pick<RentalResultsFiltersProps, 'catalog' | 'currency'>,
): ReactNode {
  const { offers, catalog, currency, value, copy, locked, onChange } = props;
  const { priceFloor, priceCeiling, categories, companies } = useRentalFilterCatalog(
    offers,
    catalog,
  );
  return (
    <Fragment>
      <FilterSection title={copy.policyLabel}>
        <SwitchFilterRow
          label={copy.withinPolicyLabel}
          checked={value.policyOnly}
          disabled={locked}
          onChange={(policyOnly) => {
            onChange({ ...value, policyOnly });
          }}
        />
      </FilterSection>
      <PanelSection title={copy.accessoriesLabel}>
        <div className="grid gap-2">
          {RENTAL_ACCESSORIES.map((accessory) => (
            <SwitchFilterRow
              key={accessory}
              label={copy.accessoryLabel(accessory)}
              checked={value.accessories.includes(accessory)}
              disabled={locked}
              onChange={() => {
                onChange({ ...value, accessories: toggled(value.accessories, accessory) });
              }}
            />
          ))}
        </div>
      </PanelSection>
      {priceFloor !== undefined && priceCeiling !== undefined && (
        <Fragment>
          <Separator />
          <RentalDailyPriceRange
            floor={priceFloor}
            ceiling={priceCeiling}
            currency={currency}
            value={value}
            copy={copy}
            locked={locked}
            onChange={onChange}
          />
        </Fragment>
      )}
      {categories.length > 0 && (
        <PanelSection title={copy.categoryLabel}>
          <RentalCategoryRows {...props} categories={categories} />
        </PanelSection>
      )}
      {companies.length > 0 && (
        <PanelSection title={copy.companyLabel}>
          <RentalCompanyRows {...props} companies={companies} />
        </PanelSection>
      )}
    </Fragment>
  );
}

/**
 * A coluna de cortes do carro — o painel da casca (`ResultsFiltersPanel`)
 * com os eixos do painel da #5125: Política, Acessórios (ar e câmbio
 * automático), Diária, Categoria (com o menor total de cada uma) e Agência.
 */
export function RentalResultsFilters({
  offers,
  catalog,
  currency,
  value,
  copy,
  locked = false,
  countsPartial = false,
  agencyPinned = false,
  onAgencyUnpin,
  onChange,
  // O que sobra é a casca do painel (`frame`, `header`, `dismiss`, o recolher):
  // repassado inteiro, os defaults são os do próprio painel.
  ...panel
}: Readonly<RentalResultsFiltersProps>): ReactNode {
  return (
    <ResultsFiltersPanel
      {...panel}
      copy={copy}
      active={agencyPinned || hasActiveFilters(value)}
      locked={locked}
      onReset={() => {
        onChange(EMPTY_RENTAL_RESULTS_FILTERS);
        if (agencyPinned) onAgencyUnpin?.();
      }}
    >
      <RentalResultsFiltersBody
        offers={offers}
        {...(catalog ? { catalog } : {})}
        currency={currency}
        value={value}
        copy={copy}
        locked={locked}
        countsPartial={countsPartial}
        onChange={onChange}
      />
    </ResultsFiltersPanel>
  );
}
